import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { app, storage } from './firebase';
import type { DocumentRecord, EnvelopeCreate, Recipient, RecipientInput, SendReadiness } from '@beckersign/shared';

const fns = getFunctions(app, 'us-west2');

export const createEnvelope = httpsCallable<EnvelopeCreate, { envelopeId: string }>(
  fns, 'createEnvelope'
);
export const verifyAuditChain = httpsCallable<
  { envelopeId: string },
  { valid: boolean; eventCount: number; problems: string[] }
>(fns, 'verifyAuditChain');

export const saveRecipients = httpsCallable<
  { envelopeId: string; recipients: RecipientInput[] },
  { recipients: Recipient[]; issuedCodes: { email: string; name: string; code: string }[]; readiness: SendReadiness }
>(fns, 'saveRecipients');

export const sendEnvelope = httpsCallable<
  { envelopeId: string },
  { status: string; notified: number; failures: string[]; ccCount: number }
>(fns, 'sendEnvelope');

export const resendToRecipient = httpsCallable<
  { envelopeId: string; recipientId: string }, { ok: boolean }
>(fns, 'resendToRecipient');

export const regenerateAccessCode = httpsCallable<
  { envelopeId: string; recipientId: string }, { code: string }
>(fns, 'regenerateAccessCode');

export const getDocumentUrl = httpsCallable<
  { envelopeId: string; documentId: string; kind?: 'original' | 'final' },
  { url: string; name: string }
>(fns, 'getDocumentUrl');

export const getCertificateUrl = httpsCallable<{ envelopeId: string }, { url: string }>(
  fns, 'getCertificateUrl'
);

const finalize = httpsCallable<
  { envelopeId: string; documentId: string; fileName: string },
  { document: DocumentRecord }
>(fns, 'finalizeDocumentUpload');

/** Client-side id generation; the server never trusts it for anything but the path. */
function newId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 20);
}

/**
 * Uploads straight to Storage under the narrow owner rule, then asks the server
 * to validate, hash and record it.
 *
 * The client deliberately does no PDF inspection: page counts, encryption checks
 * and the SHA-256 all come from the server, because a client-reported hash would
 * be worthless as evidence.
 */
export async function uploadDocument(
  envelopeId: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<DocumentRecord> {
  const documentId = newId();
  const path = `envelopes/${envelopeId}/original/${documentId}.pdf`;
  const task = uploadBytesResumable(ref(storage, path), file, {
    contentType: 'application/pdf',
    customMetadata: { originalName: file.name },
  });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (s) => onProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
      reject,
      () => resolve()
    );
  });

  const res = await finalize({ envelopeId, documentId, fileName: file.name });
  return res.data.document;
}

export const removeDocument = httpsCallable<
  { envelopeId: string; documentId: string; replacedBy?: string },
  { fieldsMoved: number; fieldsDropped: number }
>(fns, 'removeDocument');

/**
 * Uploads `file` as a new document, then removes `documentId` in its favour so
 * the old document's fields carry over. If the upload is rejected, the old
 * document is left untouched.
 */
export async function replaceDocument(
  envelopeId: string,
  documentId: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<{ fieldsMoved: number; fieldsDropped: number }> {
  const added = await uploadDocument(envelopeId, file, onProgress);
  const res = await removeDocument({ envelopeId, documentId, replacedBy: added.id });
  return res.data;
}
